/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ChevronDownIcon } from '@coursera/cds-icons';

// Shared trigger "pill" for filter/sort bars, matching the design exactly: 16px label,
// 12px gap to a 24px chevron, a 20px leading icon, and a bottom-border that darkens when open.
// (CDS MultiSelect/SilentSelectField hard-code a 14px label + 4px gap internally, which is why
// these triggers are custom rather than CDS selects.)
const styles = {
  pill: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    min-height: 48px;
    padding: var(--cds-spacing-150) 0 10px;
    color: var(--cds-color-neutral-primary-weak);
    font-family: inherit;
    font-size: 16px;
    line-height: 24px;
    white-space: nowrap;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-bottom: 2px solid transparent;
    border-radius: 4px 4px 0 0;

    &:hover {
      border-bottom-color: var(--cds-color-neutral-stroke-primary-weak);
    }

    &[aria-expanded='true'] {
      border-bottom-color: var(--cds-color-neutral-primary);
    }

    svg {
      display: block;
    }
  `,
  labelGroup: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  leadingIcon: css`
    display: inline-flex;

    svg {
      width: 20px;
      height: 20px;
    }
  `,
  caret: css`
    display: inline-flex;
    flex: 0 0 auto;

    svg {
      width: 24px;
      height: 24px;
    }
  `,
  chip: css`
    padding: 1px var(--cds-spacing-100) 2px;
    color: var(--cds-color-neutral-primary-weak);
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    background: var(--cds-color-neutral-background-primary-weak);
    border-radius: 999px;
  `,
};

export type FilterPillProps = {
  label: string;
  leadingIcon?: React.ReactNode;
  /** Optional value shown as a rounded chip after the label (e.g. "All" or a count). */
  chip?: React.ReactNode;
  open: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  'aria-label'?: string;
  'data-testid'?: string;
};

/**
 * Shared trigger button for enterprise filter/sort bars. Renders a label (with an optional
 * leading icon and trailing chip) plus a chevron, styled to sit alongside CDS silent
 * MultiSelect triggers. Forwards its ref so a popover can anchor to it.
 */
const FilterPill = React.forwardRef<HTMLButtonElement, FilterPillProps>(function FilterPillImpl(
  { label, leadingIcon, chip, open, onClick, 'aria-label': ariaLabel, 'data-testid': dataTestId },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      css={styles.pill}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      <span css={styles.labelGroup}>
        {leadingIcon ? <span css={styles.leadingIcon}>{leadingIcon}</span> : null}
        {label}
        {chip !== undefined && chip !== null ? <span css={styles.chip}>{chip}</span> : null}
      </span>
      <span css={styles.caret}>
        <ChevronDownIcon />
      </span>
    </button>
  );
});

export default FilterPill;
